import { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';

interface HeroSectionProps {
  onScrollTo: (id: string) => void;
}

export default function HeroSection({ onScrollTo }: HeroSectionProps) {
  const [visible, setVisible] = useState(false);
  const [line1, setLine1] = useState(false);
  const [line2, setLine2] = useState(false);
  const [line3, setLine3] = useState(false);

  useEffect(() => {
    const t1 = setTimeout(() => setVisible(true), 100);
    const t2 = setTimeout(() => setLine1(true), 400);
    const t3 = setTimeout(() => setLine2(true), 700);
    const t4 = setTimeout(() => setLine3(true), 1000);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); clearTimeout(t4); };
  }, []);

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden bg-[#080808]">
      {/* Background Image */}
      <div className="absolute inset-0 w-full h-full">
        <img
          src="https://public.readdy.ai/ai/img_res/04a79318c56c6587f7e2145e41ebc506.jpg"
          alt="Khalsa Healing luxury therapy session in Espanola NM"
          className="w-full h-full object-cover object-center"
        />
      </div>

      {/* Layered overlays for depth */}
      <div className="absolute inset-0 bg-gradient-to-r from-[#080808]/95 via-[#080808]/70 to-[#080808]/10" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#080808] via-transparent to-[#080808]/40" />
      <div className="absolute inset-0 bg-gradient-to-b from-[#080808]/50 via-transparent to-transparent" />

      {/* Decorative vertical gold line */}
      <div
        className={`absolute left-[4%] top-1/4 bottom-1/4 w-px bg-gradient-to-b from-transparent via-[#d4af37]/40 to-transparent transition-all duration-1000 ${visible ? 'opacity-100' : 'opacity-0'}`}
        style={{ transitionDelay: '600ms' }}
      />

      {/* Content */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 md:px-10 lg:px-16 pt-36 pb-24">
        <div className="max-w-3xl">
          {/* Eyebrow */}
          <div
            className={`flex items-center gap-4 mb-8 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
          >
            <span className="w-8 h-px bg-[#d4af37]/60" />
            <p
              className="text-[#d4af37] uppercase tracking-[0.3em] text-[10px]"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              Khalsa Healing Arts &bull; Yardley, Pennsylvania
            </p>
          </div>

          {/* H1 - three staggered lines */}
          <h1 style={{ fontFamily: 'Cormorant Garamond, serif' }} className="font-light leading-[1.05] mb-10">
            <span
              className={`block text-5xl md:text-6xl lg:text-[76px] text-white transition-all duration-700 ${line1 ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4'}`}
            >
              Elevated Healing
            </span>
            <span
              className={`block text-5xl md:text-6xl lg:text-[76px] text-[#d4af37] italic transition-all duration-700 ${line2 ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4'}`}
              style={{ transitionDelay: '0ms' }}
            >
              Experiences.
            </span>
            <span
              className={`block text-4xl md:text-5xl lg:text-[54px] text-white/70 transition-all duration-700 ${line3 ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4'}`}
              style={{ transitionDelay: '0ms' }}
            >
              Precision. Presence. Transformation.
            </span>
          </h1>

          <p
            className={`text-[#8a8a8a] text-lg leading-relaxed max-w-xl mb-12 font-light transition-all duration-700 ${line3 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
            style={{ fontFamily: 'Inter, sans-serif', transitionDelay: '200ms' }}
          >
            A refined approach to healing designed to restore balance, clarity, and long-term well-being through advanced therapeutic practices in Yardley, PA and Bucks County.
          </p>

          <div
            className={`flex flex-col sm:flex-row gap-4 mb-16 transition-all duration-700 ${line3 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
            style={{ transitionDelay: '350ms' }}
          >
            <button
              onClick={() => onScrollTo('booking')}
              className="inline-flex items-center justify-center gap-2.5 bg-[#d4af37] hover:bg-[#c09b2a] text-[#080808] px-10 py-4.5 rounded-full text-xs uppercase tracking-[0.18em] transition-all duration-300 cursor-pointer whitespace-nowrap font-semibold"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              Schedule Your Session
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line text-sm" />
              </div>
            </button>
            <Link
              to="/services"
              className="inline-flex items-center justify-center gap-2 border border-white/20 hover:border-[#d4af37]/50 text-white/70 hover:text-[#d4af37] px-10 py-4.5 rounded-full text-xs uppercase tracking-[0.15em] transition-all duration-300 cursor-pointer whitespace-nowrap"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              Explore Services
            </Link>
          </div>

          {/* Stats row */}
          <div
            className={`flex flex-wrap gap-8 transition-all duration-700 ${line3 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
            style={{ transitionDelay: '500ms' }}
          >
            {[
              { val: '500+', label: 'Sessions Completed' },
              { val: '5.0★', label: 'Average Rating' },
              { val: '4+', label: 'Healing Modalities' },
            ].map((stat, i) => (
              <div key={i} className="flex items-center gap-3">
                <span
                  className="text-white text-2xl font-light"
                  style={{ fontFamily: 'Cormorant Garamond, serif' }}
                >
                  {stat.val}
                </span>
                <span
                  className="text-[#4a4a4a] text-xs uppercase tracking-[0.1em] border-l border-[#2a2a2a] pl-3"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  {stat.label}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-10 right-10 z-10 hidden md:flex flex-col items-center gap-3 opacity-40">
        <span
          className="text-white text-[9px] uppercase tracking-[0.3em] rotate-90 origin-center"
          style={{ fontFamily: 'Inter, sans-serif' }}
        >
          Scroll
        </span>
        <div className="w-px h-12 bg-gradient-to-b from-white to-transparent animate-pulse" />
      </div>
    </section>
  );
}
